import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, SendHorizontal, Copy } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import challenge_invite from "/src/assets/challenge-invite.png";
import axios from "axios";
import { toast } from "react-toastify";

export const ChallengeFriend = () => {
  const userId = localStorage.getItem("userId");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userIdInput, setUserIdInput] = useState("");
  const [opponentId, setOpponentId] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [friendList, setFriendList] = useState([]);
  const [challengeMode, setChallengeMode] = useState("sync");
  const [opponentHandle, setOpponentHandle] = useState("");
  const [isChallenging, setIsChallenging] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subject = searchParams.get("subject");
  const topic = searchParams.get("topic");
  const level = "medium";
  const subtopic = searchParams.get("subtopic");

  const baseUrl = import.meta.env.VITE_APP_BASE_URL;

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const res = await axios.get(`${baseUrl}/v2/user/${userId}/friends`);
        setFriendList(res.data.friends);
      } catch (error) {
        console.error("Failed to fetch friends:", error);
      }
    };
    fetchFriends();
  }, [baseUrl, userId]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setUserIdInput("");
    setInviteLink("");
  };

  const inviteByLink = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/play/challenges`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          creatorId: userId,
          subject,
          topic,
          level,
          subtopic,
          mode: "async",
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to create challenge link.");
      }

      const data = await res.json();
      const fullInviteLink = `${window.location.origin}/challenge/join/${data.inviteCode}`;
      setInviteLink(fullInviteLink);
      navigate(`/challenge/waiting/${data.challengeId}`);
    } catch (error) {
      toast.error(`Failed to generate challenge link: ${error.message}`);
      console.error(error);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success("Copied to clipboard!");
  };

  const inviteByUserId = async () => {
    if (!userIdInput) {
      toast.warn("Please enter a friend's User ID.");
      return;
    }
    try {
      const res = await axios.post(`${baseUrl}/v2/user/add-friend`, {
        user_id: userId,
        friend_username: userIdInput,
      });

      if (res.status === 200 && res.data.friend_id) {
        setOpponentId(res.data.friend_id);
        setOpponentHandle(userIdInput);
        closeModal();
      } else {
        toast.error(res.data.message || "Failed to add friend.");
      }
    } catch (err) {
      console.error("Add friend error:", err);
      toast.error(
        err.response?.data?.detail ||
          "Could not add friend. Please check the User ID."
      );
    }
  };

  const handleChallengeFriend = async () => {
    if (!subject || !topic || !opponentId) {
      toast.warn("Please select a subject, topic, and opponent.");
      return;
    }
    setIsChallenging(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/play/challenges`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          creatorId: userId,
          opponentId,
          subject,
          topic,
          level,
          subtopic,
          mode: challengeMode,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to create challenge.");
      }
      const data = await res.json();
      if (challengeMode === "sync") {
        navigate(`/challenge/waiting/${data.challengeId}`);
      } else {
        toast.info("Async challenge sent! Your friend can respond later.");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(`Error creating challenge: ${error.message}`);
      console.error(error);
    } finally {
      setIsChallenging(false);
    }
  };

  return (
    <div className="flex flex-col items-center p-4 w-full">
      <div className="flex flex-col sm:flex-row justify-center gap-2 mt-5 w-full mb-8">
        <button className="flex-1 bg-[#A495FF99] border-2 border-[#0D99FF] text-[#0D99FF] py-2.5 rounded-md cursor-pointer h-[50px] sm:h-[60px] font-bold transition-colors duration-200 hover:bg-[#0D99FF] hover:text-white text-sm sm:text-base">
          Play with Friends
        </button>
        <button
          className="flex-1 bg-gray-200 text-gray-700 border-none py-2.5 px-5 rounded-md cursor-pointer h-[50px] sm:h-[60px] text-sm sm:text-base"
          onClick={() => navigate("/play/across-india")}
        >
          Play across India
        </button>
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-5 mb-8 w-full">
        <div className="bg-[#F2F0FF82] p-4 rounded-lg shadow-sm border border-black/[0.5] w-[80%] sm:w-[250px] h-[250px] flex flex-col justify-between items-center">
          <img
            src="/assets/images/You.png"
            alt="You"
            className="w-[120px] sm:w-[200px] h-[120px] sm:h-[200px] object-cover"
          />
          <p className="font-bold text-gray-900 text-sm sm:text-base">You</p>
        </div>
        <div className="text-xl sm:text-2xl text-gray-600 font-bold my-4 sm:my-0">
          VS
        </div>
        <div className="bg-[#F2F0FF82] py-2 rounded-[10px] shadow-md border border-black w-[80%] sm:w-[250px] h-[250px] flex flex-col justify-between">
          {opponentId ? (
            <div className="flex flex-col items-center justify-center h-full space-y-2 p-4">
              <img
                src={
                  friendList.find((f) => f.id === opponentId)?.profile ||
                  "/assets/images/You.png"
                }
                alt="Selected Friend"
                className="w-12 h-12 rounded-full object-cover"
              />
              <p className="text-sm font-semibold text-gray-800">
                {(() => {
                  const selectedFriend = friendList.find(
                    (f) => f.id === opponentId
                  );
                  const name =
                    selectedFriend?.fullName ||
                    opponentHandle ||
                    "Unknown User";
                  return name
                    ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
                    : "";
                })()}
              </p>
              <p className="text-xs text-green-600">Friend Selected</p>
            </div>
          ) : (
            <div className="overflow-y-auto h-full w-full px-2 py-3">
              {friendList.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-xs sm:text-sm text-gray-500 text-center">
                    No friends. Add new friends!
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {friendList.map((friend) => (
                    <div
                      onClick={() => {
                        setOpponentHandle(friend.handle);
                        setOpponentId(friend.id);
                      }}
                      key={friend.id}
                      className="flex items-center space-x-3 bg-white p-2 rounded-lg shadow-sm hover:bg-gray-100 transition cursor-pointer"
                    >
                      <img
                        src={friend.profile || "/assets/images/You.png"}
                        alt={friend.fullName}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-xs sm:text-sm font-medium text-gray-800">
                        {friend.fullName
                          ? friend.fullName.charAt(0).toUpperCase() +
                            friend.fullName.slice(1).toLowerCase()
                          : ""}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!opponentId && (
        <button
          className="bg-[#0D99FF] text-white rounded-3xl border-none cursor-pointer font-bold py-2 px-3 sm:py-2.5 sm:px-4 text-xs sm:text-md transition-colors duration-200 hover:bg-purple-700 block mt-3"
          onClick={openModal}
        >
          + Add New Friend
        </button>
      )}
      {opponentId && (
        <button
          className="bg-[#0D99FF] text-white border-none py-2 px-3 sm:py-2.5 sm:px-4 rounded-full cursor-pointer font-bold text-xs sm:text-md transition-colors duration-200 hover:bg-purple-700 block mt-3"
          onClick={handleChallengeFriend}
          disabled={isChallenging}
        >
          {isChallenging
            ? "Challenging your friend..."
            : "Challenge This Friend Now!"}
        </button>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeModal}
            />
            <motion.div
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-4 sm:p-5 rounded-lg shadow-lg w-[90%] max-w-md text-center z-[1001]"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <h2 className="text-purple-700 text-base sm:text-lg font-bold mb-4 sm:mb-5">
                Add New Friend
              </h2>
              <div className="flex flex-col sm:flex-row items-center gap-3 rounded-xl border border-gray-300 p-3 mb-4">
                <img
                  src={challenge_invite}
                  alt="Challenge Invite"
                  className="w-12 h-auto"
                />
                <div className="text-left flex-1">
                  <div className="font-bold text-gray-800 text-xs sm:text-base mb-1">
                    Challenge Friend Via Link
                  </div>
                  <p className="text-gray-600 text-xs sm:text-sm">
                    Use the link to add your friend to play
                  </p>
                </div>
                <button
                  className="bg-white text-black border-none cursor-pointer flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  onClick={inviteByLink}
                >
                  <Share2 size={18} />
                </button>
              </div>
              {inviteLink && (
                <div className="flex flex-col sm:flex-row items-center bg-gray-100 rounded-md px-3 py-2 mb-4">
                  <input
                    type="text"
                    value={inviteLink}
                    readOnly
                    className="flex-1 bg-transparent border-none outline-none text-gray-800 text-xs sm:text-sm mb-2 sm:mb-0"
                  />
                  <button
                    className="bg-purple-600 text-white p-2 rounded-md hover:bg-purple-700 transition-colors duration-200"
                    onClick={copyToClipboard}
                  >
                    <Copy size={16} />
                  </button>
                </div>
              )}
              <div className="text-gray-700 font-semibold my-3 sm:my-4 text-xs sm:text-sm">
                or
              </div>
              <div className="font-bold text-gray-800 text-xs sm:text-base mb-2 sm:mb-3 text-left">
                Add Friend by their User ID
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
                <input
                  type="text"
                  placeholder="Enter Friends User ID"
                  className="p-2 border border-gray-300 rounded-lg flex-1 text-xs sm:text-sm focus:ring-purple-500 focus:border-purple-500"
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value)}
                />
                <button
                  className="bg-purple-600 text-white p-2 rounded-lg hover:bg-purple-700 transition-colors duration-200"
                  onClick={inviteByUserId}
                >
                  <SendHorizontal size={18} />
                </button>
              </div>
              <button
                className="bg-white text-gray-700 border border-gray-300 py-2 px-4 rounded-full cursor-pointer mt-4 hover:bg-gray-100 transition-colors duration-200 text-xs sm:text-sm"
                onClick={closeModal}
              >
                Cancel
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChallengeFriend;
